import React, { memo, useMemo, useState } from 'react';
import { combineClassNames } from '@common/utils/combineClassNames';
import { usePerformanceStore } from '@modules/performance/store/store';
import { Button } from '@common/components/button';
import { TextInput } from '@common/components/form/inputs/text';
import { useTranslation } from 'react-i18next';

export type ChecklistScreenComponentValue = string[];

type ChecklistScreenComponentOwnProps = {
  item: any;
  onToggle?: (optionId: string) => void;
  value?: ChecklistScreenComponentValue;
};

export const ChecklistScreenComponent = memo(
  ({ item }: ChecklistScreenComponentOwnProps) => {
    const { t } = useTranslation();
    const { competencyData } = usePerformanceStore();

    const allowedCategoryIds: string[] = useMemo(() => {
      const ids = item?.settings?.competencyIds || item?.settings?.category_ids;
      return Array.isArray(ids) ? ids.map((v: any) => String(v)) : [];
    }, [item?.settings]);

    const groups: any[] = useMemo(() => {
      const list = Array.isArray(competencyData) ? competencyData : [];
      if (!allowedCategoryIds.length) return [];
      return list.filter((g: any) => allowedCategoryIds.includes(String(g?.category?.id))
      );
    }, [competencyData, allowedCategoryIds]);

    const selectedCompetencies = useMemo(() => {
      const list = Array.isArray(groups) ? groups : [];
      const comps = list.flatMap((g: any) => (Array.isArray(g?.competencies) ? g.competencies : [])
      );
      return comps;
    }, [groups]);

    const [selectedScaleByCompetency, setSelectedScaleByCompetency] = useState<
      Record<string, string>
    >({});

    const handleSelectScale = (competencyId: string, scaleId: string) => {
      setSelectedScaleByCompetency((prev) => ({
        ...prev,
        [String(competencyId)]: String(scaleId),
      }));
    };

    const getSortedScales = (comp: any) => {
      return Array.isArray(comp?.rating_scales)
        ? [...comp.rating_scales].sort(
            (a, b) => (a.index || 0) - (b.index || 0)
          )
        : [];
    };

    return (
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-5 tw-w-full">
          {selectedCompetencies.map((comp: any) => {
            const scales = getSortedScales(comp);
            const selectedScaleId = selectedScaleByCompetency[String(comp.id)];
            const selectedScale = scales.find(
              (s) => String(s.id) === String(selectedScaleId)
            );

            return (
              <div key={comp.id} className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-text-base tw-font-medium tw-text-gray-900 tw-text-left">
                  {comp.name}
                </div>
                <div className="tw-text-sm tw-whitespace-pre-line tw-text-gray-600 tw-text-left">
                  {comp.description}
                </div>

                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                  <div className="tw-flex tw-flex-row tw-items-center tw-w-full">
                    {scales.map((scale, idx) => {
                      const isSelected =
                        String(scale.id) === String(selectedScaleId);
                      return (
                        <Button
                          key={scale.id}
                          type={isSelected ? 'primary' : 'inverted'}
                          size="large"
                          className={combineClassNames(
                            'tw-rounded tw-border tw-text-sm tw-font-medium tw-w-full tw-cursor-pointer'
                          )}
                          style={
                            isSelected && scale?.color
                              ? {
                                  backgroundColor: scale.color,
                                }
                              : undefined
                          }
                          onClick={() => handleSelectScale(String(comp.id), String(scale.id))}
                        >
                          {idx + 1}
                        </Button>
                      );
                    })}
                  </div>
                  {selectedScale && (
                    <div className="tw-text-center tw-text-gray-textParagraph tw-text-sm tw-font-medium">
                      {selectedScale.label}
                    </div>
                  )}
                </div>

                <TextInput
                  placeholder={t(
                    'learning:component_type_question_text_placeholder'
                  )}
                  className="tw-mt-2"
                />
                <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
